export * from "ce/selectors/modulesSelector";
import type { DefaultRootState } from "react-redux";
import { MODULE_TYPE, type Module } from "ee/constants/ModuleConstants";
import type { Action } from "entities/Action";
import { createSelector } from "reselect";
import { countBy } from "lodash";
import type { ModuleInstanceEntitiesReducerState } from "ee/reducers/entityReducers/moduleInstanceEntitiesReducer";
import { getShowUIModule } from "./moduleFeatureSelectors";
import { getModulesMetadata } from "./packageSelectors";
import type { ModulesReducerState } from "ee/reducers/entityReducers/modulesReducer";

const DEFAULT_INPUT_EVAL_VALUES = {};
const DEFAULT_OUTPUT_EVAL_VALUES = {};

/**
 * This is a duplicated selector from entitiesSelector and the reason for
 * duplication is the entitiesSelector is referencing the modulesSelector and
 * if the getDatasources is referenced from entitiesSelect, a cyclic dependency is
 * created. This has to be resolved more gracefully.
 *
 * Since appropriate typings are in place, if the structure changes then we would get typing error here.
 *  */
export const getDatasources = (state: DefaultRootState) => {
  return state.entities.datasources.list;
};

export const getRawModules = (state: DefaultRootState) =>
  state.entities.modules;

export const getAllModules = createSelector(
  getRawModules,
  getModulesMetadata,
  (modules, modulesMetadata): ModulesReducerState => {
    return Object.values(modules).reduce((acc, module) => {
      const metadata = modulesMetadata?.[module.id] || {};

      acc[module.id] = {
        ...module,
        ...metadata,
        actionId: metadata?.publicEntity?.baseId,
      };

      return acc;
    }, {} as ModulesReducerState);
  },
);

export const getAllQueryModules = createSelector(getAllModules, (modules) => {
  const queryModules = Object.values(modules).filter(
    (module) => module.type === MODULE_TYPE.QUERY,
  );

  return queryModules;
});

export const getAllJSModules = createSelector(getAllModules, (modules) => {
  return Object.values(modules).filter(
    (module) => module.type === MODULE_TYPE.JS,
  );
});

export const getCurrentModuleId = (state: DefaultRootState) =>
  state.ui.editor.currentModuleId || "";

export const getCurrentBaseModuleId = (state: DefaultRootState) =>
  state.ui.editor.currentBaseModuleId || "";

export const getCurrentModule = createSelector(
  getAllModules,
  getCurrentModuleId,
  (modules, moduleId): Module | undefined => modules[moduleId],
);

export const getModulePermissions = createSelector(
  getAllModules,
  getCurrentModuleId,
  (modules, moduleId) => modules[moduleId]?.userPermissions || [],
);

export const getModuleActions = (state: DefaultRootState) =>
  state.entities.actions;

export const getModuleJSCollections = (state: DefaultRootState) =>
  state.entities.jsActions;

export const getModuleById = createSelector(
  getAllModules,
  (state: DefaultRootState, moduleId: string) => moduleId,
  (modules, moduleId): Module | undefined => modules[moduleId],
);

export const getModuleByBaseId = createSelector(
  getAllModules,
  (state: DefaultRootState, baseId: string) => baseId,
  (modules, baseId) =>
    Object.values(modules).find((module) => module.baseId === baseId),
);

export const getActionsInCurrentModule = createSelector(
  getModuleActions,
  getCurrentModuleId,
  (actions, moduleId) => {
    if (!moduleId) return [];

    return actions;
  },
);

export const getJSCollectionsInCurrentModule = createSelector(
  getModuleJSCollections,
  getCurrentModuleId,
  (jsCollections, moduleId) => {
    if (!moduleId) return [];

    return jsCollections;
  },
);

export const getIsModuleFetchingEntities = (state: DefaultRootState) =>
  state.ui.editor.isModuleFetchingEntities;

export const getModulePublicAction = (
  state: DefaultRootState,
  moduleId: string,
): Action | undefined => {
  const action = state.entities.actions.find(
    (action) => action.config.moduleId === moduleId && action.config.isPublic,
  );

  return action ? action.config : undefined;
};

export const getModulePublicJSCollection = (
  state: DefaultRootState,
  moduleId: string,
) => {
  const action = state.entities.jsActions.find(
    (action) => action.config.moduleId === moduleId && action.config.isPublic,
  );

  return action ? action.config : undefined;
};

export const getIsModuleSaving = (state: DefaultRootState) => {
  return state.ui.editor.isModuleUpdating;
};

export const getIsModuleReferenceUpdating = (state: DefaultRootState) => {
  return state.ui.editor.isModuleReferenceUpdating;
};

export const getHasModuleUpdateError = (state: DefaultRootState) => {
  return state.ui.editor.hasModuleUpdateError;
};

export const getModuleInputsEvalValues = (state: DefaultRootState) =>
  state.evaluations.tree?.inputs || DEFAULT_INPUT_EVAL_VALUES;

export const getModuleOutputsEvalValues = (state: DefaultRootState) =>
  state.evaluations.tree?.outputs || DEFAULT_OUTPUT_EVAL_VALUES;

export const getModuleInstanceActions = (
  state: DefaultRootState,
): ModuleInstanceEntitiesReducerState["actions"] =>
  state.entities.moduleInstanceEntities.actions;

export const getModuleInstanceJSCollections = (
  state: DefaultRootState,
): ModuleInstanceEntitiesReducerState["jsCollections"] =>
  state.entities.moduleInstanceEntities.jsCollections;

export const getModuleDSUsage = createSelector(
  getAllModules,
  getDatasources,
  (modules, datasources) => {
    const actionCount = countBy(Object.values(modules), "datasourceId");
    const actionDsMap: Record<string, string> = {};

    datasources.forEach((ds) => {
      actionDsMap[ds.id] = `No modules in this package`;
    });
    Object.keys(actionCount).forEach((dsId) => {
      actionDsMap[dsId] = `${actionCount[dsId]} modules in this package`;
    });

    return actionDsMap;
  },
);

export const getCurrentProcessingModuleReferences = (state: DefaultRootState) =>
  state.ui.editor.currentProcessingModuleReferences;

export const getHasPrivateEntity = createSelector(
  getModuleActions,
  getModuleJSCollections,
  getCurrentModule,
  (moduleActions, moduleJSCollections, currentModule) => {
    const hasPrivateActions = moduleActions.some(
      (action) =>
        action.config.moduleId === currentModule?.id && !action.config.isPublic,
    );
    const hasPrivateJSCollection = moduleJSCollections.some(
      (jsCollection) =>
        jsCollection.config.moduleId === currentModule?.id &&
        !jsCollection.config.isPublic,
    );

    return hasPrivateActions || hasPrivateJSCollection;
  },
);

export const getPrivateEntitiesNames = createSelector(
  getModuleActions,
  getModuleJSCollections,
  getCurrentModule,
  (moduleActions, moduleJSCollections, currentModule) => {
    const actionNames = moduleActions
      .filter(
        (a) => a.config.moduleId === currentModule?.id && a.config.isPublic,
      )
      .map((a) => a.config.name);
    const jsCollectionNames = moduleJSCollections
      .filter(
        (a) => a.config.moduleId === currentModule?.id && a.config.isPublic,
      )
      .map((a) => a.config.name);

    return [...jsCollectionNames, ...actionNames];
  },
);

export const getIsSettingUpModule = (state: DefaultRootState) =>
  state.ui.editor.isSettingUpModule;

export const getIsFetchingConsumableModules = (state: DefaultRootState) =>
  state.ui.editor.isFetchingConsumableModules;

export const getLintErrors = (state: DefaultRootState) => {
  return state.linting.errors;
};

export const getModuleReferences = (state: DefaultRootState) => {
  return state.ui.editor.moduleReferences;
};

export const getHasCyclicModuleReference = (state: DefaultRootState) => {
  return state.ui.editor.hasCyclicModuleReference;
};

export const getCurrentModuleReferences = createSelector(
  getModuleReferences,
  getCurrentModuleId,
  (references, moduleId) => {
    return references[moduleId];
  },
);

export const getActiveModuleActions = (state: DefaultRootState) =>
  state.ui.moduleActiveActions.activeActions;

export const showUIModulesList = createSelector(
  getShowUIModule,
  getAllModules,
  (showUIModule, modules) => {
    return (
      showUIModule &&
      Object.values(modules).some((module) => module.type === MODULE_TYPE.UI)
    );
  },
);

export const getIsCreatingUIModuleInstance = (state: DefaultRootState) =>
  state.ui.editor.isCreatingUIModuleInstance;
